import React, { useState } from "react";
import toast from "react-hot-toast";
import { getInvoiceDetails } from "../../services/api";

/**
 * InvoiceDocLink
 *
 * Renders a small "Invoice" link only when the invoice has an attached document.
 * On click it calls GET /web/v1/payment-requests/details to fetch the public URL
 * and opens it in a new tab — avoids exposing a potentially short-lived S3 URL
 * stored on the invoice object.
 *
 * Props:
 *   invoiceId  {string}  — the payment request UUID
 *   docUrl     {string}  — pass inv.docUrl; renders nothing when falsy
 */
const InvoiceDocLink = ({ invoiceId, docUrl }) => {
  const [loading, setLoading] = useState(false);

  if (!docUrl) return null;

  const handleClick = async (e) => {
    e.stopPropagation(); // don't trigger row click / navigate
    if (loading) return;
    setLoading(true);
    try {
      const res = await getInvoiceDetails(invoiceId);
      // Handle multiple possible field names from the API
      const url =
        res?.data?.url ||
        res?.data?.fileUrl ||
        res?.data?.docUrl ||
        res?.data?.publicUrl ||
        res?.url;
      if (!url) throw new Error("Document URL not found in response.");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Could not open document. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      title="View attached invoice document"
      className="inline-flex items-center gap-1 text-xs text-brand hover:underline disabled:opacity-50 disabled:cursor-wait"
    >
      {loading ? (
        <>
          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Opening…
        </>
      ) : (
        <>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
          Invoice
        </>
      )}
    </button>
  );
};

export default InvoiceDocLink;
